/**
 * Schematic SVG diagram for Radial Diffusor Channel assistant parameters.
 *
 * r-z plane (r = horizontal, z = vertical up, SVG y inverted).
 * Shape: wide U opening left — high-z arm curves right to apex, drops, low-z arm returns left.
 *
 * Flow path: P3 (inner-r, high-z) → P4 → [Bezier] → P5a (apex top) → P5b (apex bottom)
 *            → [Bezier] → P6 → P7 → [Bezier] → P8 (inner-r, low-z) → L9e (outlet)
 */
export default function DiffusorParamDiagram() {
  // SVG coords: r→x (left=small, right=large), z↑ = SVG y↓ (top=high-z)
  // viewBox="0 0 420 215"  r-axis at y=197  z-axis at x=48

  const P3   = [86,  64];   // LE: inner-r, high-z
  const P4   = [165, 54];   // inlet tangent anchor (~10° from horizontal)
  const CP34 = [308, 42];   // Bezier control on inlet tangent extended to R5
  const P5a  = [308, 37];   // apex top  (outermost r, highest z)
  const P5b  = [308, 100];  // apex bottom
  const CP56 = [308, 120];  // Bezier control on return tangent
  const P6   = [228, 124];  // return tangent anchor
  const P7   = [152, 140];  // return tangent anchor 2 (~10° from horizontal)
  const CP78 = [73,  150];  // Bezier control at inner-r return
  const P8   = [73,  160];  // outlet start
  const L9e  = [73,  188];  // outlet straight end

  const pt = ([x, y]) => `${x},${y}`;

  return (
    <svg
      viewBox="0 0 420 215"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-auto"
      aria-label="Radial Diffusor Channel parameter diagram"
    >
      <defs>
        <marker id="dd-arr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
          <path d="M 0,0 L 7,3.5 L 0,7 Z" fill="#374151" />
        </marker>
        <marker id="dd-dim-s" markerWidth="6" markerHeight="6" refX="0" refY="3" orient="auto">
          <path d="M 6,0 L 0,3 L 6,6" fill="none" stroke="#6b7280" strokeWidth="1" />
        </marker>
        <marker id="dd-dim-e" markerWidth="6" markerHeight="6" refX="6" refY="3" orient="auto">
          <path d="M 0,0 L 6,3 L 0,6" fill="none" stroke="#6b7280" strokeWidth="1" />
        </marker>
      </defs>

      {/* Background */}
      <rect width="420" height="215" fill="#f8fafc" rx="6" />

      {/* ── Reference dashes ── */}
      {/* R3/R8 vertical to axis */}
      <line x1={P3[0]} y1="197" x2={P3[0]} y2={P3[1]}  stroke="#d1d5db" strokeWidth="0.8" strokeDasharray="3,3" />
      {/* R5 vertical to axis */}
      <line x1={P5a[0]} y1="197" x2={P5a[0]} y2={P5a[1]} stroke="#d1d5db" strokeWidth="0.8" strokeDasharray="3,3" />
      {/* Z3 horizontal to z-axis */}
      <line x1="48" y1={P3[1]}  x2={P3[0]}  y2={P3[1]}  stroke="#d1d5db" strokeWidth="0.8" strokeDasharray="3,3" />
      {/* Z6 horizontal to z-axis */}
      <line x1="48" y1={P6[1]}  x2={P6[0]}  y2={P6[1]}  stroke="#d1d5db" strokeWidth="0.8" strokeDasharray="3,3" />
      {/* Z8 horizontal to z-axis */}
      <line x1="48" y1={P8[1]}  x2={P8[0]}  y2={P8[1]}  stroke="#d1d5db" strokeWidth="0.8" strokeDasharray="3,3" />

      {/* ── Axes ── */}
      <line x1="48" y1="197" x2="408" y2="197" stroke="#374151" strokeWidth="1.5" markerEnd="url(#dd-arr)" />
      <line x1="48" y1="202" x2="48"  y2="12"  stroke="#374151" strokeWidth="1.5" markerEnd="url(#dd-arr)" />
      <text x="412" y="201" fontSize="12" fill="#374151" fontFamily="ui-sans-serif,system-ui,sans-serif">r</text>
      <text x="40"  y="11"  fontSize="12" fill="#374151" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="middle">z</text>

      {/* ── Flow path ── */}

      {/* Inlet straight: P3 → P4 */}
      <line x1={P3[0]}  y1={P3[1]}  x2={P4[0]}  y2={P4[1]}
            stroke="#374151" strokeWidth="2" />

      {/* Bezier 1: P4 → P5a  (inlet arm curves to apex top) */}
      <path d={`M ${pt(P4)} C ${pt(CP34)} ${pt(CP34)} ${pt(P5a)}`}
            fill="none" stroke="#1e3a8a" strokeWidth="2.5" />

      {/* Apex straight: P5a → P5b */}
      <line x1={P5a[0]} y1={P5a[1]} x2={P5b[0]} y2={P5b[1]}
            stroke="#1e3a8a" strokeWidth="2.5" />

      {/* Bezier 2: P5b → P6  (apex bottom curves back) */}
      <path d={`M ${pt(P5b)} C ${pt(CP56)} ${pt(CP56)} ${pt(P6)}`}
            fill="none" stroke="#1e3a8a" strokeWidth="2.5" />

      {/* Return straight: P6 → P7 */}
      <line x1={P6[0]} y1={P6[1]} x2={P7[0]} y2={P7[1]}
            stroke="#374151" strokeWidth="2" />

      {/* Bezier 3: P7 → P8  (return arm curves to outlet) */}
      <path d={`M ${pt(P7)} C ${pt(CP78)} ${pt(CP78)} ${pt(P8)}`}
            fill="none" stroke="#1e3a8a" strokeWidth="2.5" />

      {/* Outlet straight: P8 → L9e */}
      <line x1={P8[0]} y1={P8[1]} x2={L9e[0]} y2={L9e[1]}
            stroke="#374151" strokeWidth="2" />

      {/* ── Tangent handles (dashed) ── */}
      <line x1={P4[0]}   y1={P4[1]}   x2={CP34[0]} y2={CP34[1]} stroke="#9ca3af" strokeWidth="1.1" strokeDasharray="5,3" />
      <line x1={CP34[0]} y1={CP34[1]} x2={P5a[0]}  y2={P5a[1]}  stroke="#9ca3af" strokeWidth="1.1" strokeDasharray="5,3" />
      <line x1={P5b[0]}  y1={P5b[1]}  x2={CP56[0]} y2={CP56[1]} stroke="#9ca3af" strokeWidth="1.1" strokeDasharray="5,3" />
      <line x1={CP56[0]} y1={CP56[1]} x2={P6[0]}   y2={P6[1]}   stroke="#9ca3af" strokeWidth="1.1" strokeDasharray="5,3" />
      <line x1={P7[0]}   y1={P7[1]}   x2={CP78[0]} y2={CP78[1]} stroke="#9ca3af" strokeWidth="1.1" strokeDasharray="5,3" />
      <line x1={CP78[0]} y1={CP78[1]} x2={P8[0]}   y2={P8[1]}   stroke="#9ca3af" strokeWidth="1.1" strokeDasharray="5,3" />

      {/* ── Point markers ── */}
      {[P3, P4, P5a, P5b, P6, P7, P8].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="4.5" fill="white" stroke="#1e3a8a" strokeWidth="2" />
      ))}
      {[CP34, CP56, CP78].map(([x, y], i) => (
        <polygon key={i} points={`${x},${y-5} ${x+5},${y} ${x},${y+5} ${x-5},${y}`} fill="#1e3a8a" />
      ))}

      {/* ── Γ4 angle arc at P3 ──
           Inlet tangent P3→P4 = (79,-10) → normalized ≈ (0.992,-0.126)
           From rightward (1,0) sweeping up to tangent — CCW in SVG = sweep-flag 0
           Radial ref: (P3[0]+18, P3[1]) = (104, 64)
           Tangent ref: (P3[0]+18*0.992, P3[1]+18*(-0.126)) = (103.9, 61.7)
      */}
      <line x1={P3[0]} y1={P3[1]} x2={P3[0]+18} y2={P3[1]}
            stroke="#dc2626" strokeWidth="1" strokeDasharray="2,2" />
      <path d={`M ${P3[0]+18},${P3[1]} A 18,18 0 0,0 ${(P3[0]+17.9).toFixed(1)},${(P3[1]-2.3).toFixed(1)}`}
            fill="none" stroke="#dc2626" strokeWidth="1.5" />
      <text x={P3[0]+22} y={P3[1]-6} fontSize="10" fill="#dc2626"
            fontFamily="ui-sans-serif,system-ui,sans-serif" fontStyle="italic">Γ4</text>

      {/* ── Γ7 angle arc at P6 ──
           Return tangent P6→P7 = (-76,+16) → normalized ≈ (-0.978,+0.206)
           From leftward (-1,0) sweeping down to tangent — CW in SVG = sweep-flag 1
           Radial ref: (P6[0]-18, P6[1]) = (210, 124)
           Tangent ref: (P6[0]+18*(-0.978), P6[1]+18*(0.206)) = (210.4, 127.7)
      */}
      <line x1={P6[0]} y1={P6[1]} x2={P6[0]-18} y2={P6[1]}
            stroke="#dc2626" strokeWidth="1" strokeDasharray="2,2" />
      <path d={`M ${P6[0]-18},${P6[1]} A 18,18 0 0,1 ${(P6[0]-17.6).toFixed(1)},${(P6[1]+3.7).toFixed(1)}`}
            fill="none" stroke="#dc2626" strokeWidth="1.5" />
      <text x={P6[0]-46} y={P6[1]+14} fontSize="10" fill="#dc2626"
            fontFamily="ui-sans-serif,system-ui,sans-serif" fontStyle="italic">Γ7</text>

      {/* ── L5 dimension (apex straight) ── */}
      <line x1={P5a[0]+14} y1={P5a[1]} x2={P5b[0]+14} y2={P5b[1]}
            stroke="#6b7280" strokeWidth="1"
            markerStart="url(#dd-dim-s)" markerEnd="url(#dd-dim-e)" />
      <text x={P5a[0]+20} y={(P5a[1]+P5b[1])/2+4} fontSize="10" fill="#6b7280"
            fontFamily="ui-sans-serif,system-ui,sans-serif" fontStyle="italic">L5</text>

      {/* ── L9 dimension (outlet straight) ── */}
      <line x1={P8[0]-13} y1={P8[1]} x2={L9e[0]-13} y2={L9e[1]}
            stroke="#6b7280" strokeWidth="1"
            markerStart="url(#dd-dim-s)" markerEnd="url(#dd-dim-e)" />
      <text x={P8[0]-24} y={(P8[1]+L9e[1])/2+4} fontSize="10" fill="#6b7280"
            fontFamily="ui-sans-serif,system-ui,sans-serif" fontStyle="italic" textAnchor="middle">L9</text>

      {/* ── r-axis labels ── */}
      <text x={P3[0]}   y="210" fontSize="10" fill="#1e40af" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="middle" fontStyle="italic">R3</text>
      <text x={P5a[0]}  y="210" fontSize="10" fill="#1e40af" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="middle" fontStyle="italic">R5</text>
      {/* R4, R6, R7 as near-point annotations to avoid axis clutter */}
      <text x={P4[0]}   y={P4[1]-8} fontSize="9" fill="#1e40af" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="middle" fontStyle="italic">R4</text>
      <text x={P6[0]}   y={P6[1]+16} fontSize="9" fill="#1e40af" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="middle" fontStyle="italic">R6</text>
      <text x={P7[0]}   y={P7[1]+16} fontSize="9" fill="#1e40af" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="middle" fontStyle="italic">R7</text>
      {/* R8 same x as R3; offset label */}
      <text x={P8[0]-16} y="210" fontSize="9" fill="#1e40af" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="middle" fontStyle="italic">R8</text>

      {/* ── z-axis labels ── */}
      <text x="44" y={P3[1]+4}  fontSize="10" fill="#1e40af" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="end" fontStyle="italic">Z3</text>
      <text x="44" y={P6[1]+4}  fontSize="10" fill="#1e40af" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="end" fontStyle="italic">Z6</text>
      <text x="44" y={P8[1]+4}  fontSize="10" fill="#1e40af" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="end" fontStyle="italic">Z8</text>
      {/* Z5 near apex */}
      <text x={P5a[0]+20} y={(P5a[1]+P5b[1])/2-14} fontSize="9" fill="#1e40af" fontFamily="ui-sans-serif,system-ui,sans-serif" fontStyle="italic">Z5</text>

      {/* ── Point labels ── */}
      <text x={P3[0]-6}  y={P3[1]-7}  fontSize="9" fill="#1e3a8a" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="middle">P3</text>
      <text x={P4[0]}    y={P4[1]-10} fontSize="9" fill="#1e3a8a" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="middle">P4</text>
      <text x={P5a[0]-7} y={P5a[1]-4} fontSize="9" fill="#1e3a8a" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="end">P5a</text>
      <text x={P5b[0]-7} y={P5b[1]+10} fontSize="9" fill="#1e3a8a" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="end">P5b</text>
      <text x={P6[0]+6}  y={P6[1]-5}  fontSize="9" fill="#1e3a8a" fontFamily="ui-sans-serif,system-ui,sans-serif">P6</text>
      <text x={P7[0]+6}  y={P7[1]-5}  fontSize="9" fill="#1e3a8a" fontFamily="ui-sans-serif,system-ui,sans-serif">P7</text>
      <text x={P8[0]+6}  y={P8[1]+4}  fontSize="9" fill="#1e3a8a" fontFamily="ui-sans-serif,system-ui,sans-serif">P8</text>
    </svg>
  );
}
